import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useDeleteProject,
    useProject,
    useRestoreProject,
    useUpdateProject,
} from '@/services/project.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { LoaderIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Section from '../../components/Section';
import ConfirmDialog from '../../components/ConfirmDialog';
import RoleList from '../Role/RoleList';
import { BASE } from '../../basePath';

export default function ProjectSettings() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { mutate: updateProject, isPending } = useUpdateProject();
    const { mutate: deleteProject } = useDeleteProject();
    const { mutate: restoreProject, isPending: isRestoring } =
        useRestoreProject();
    const { data: project } = useProject(projectId);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (project) {
            setName(project.name);
            setDescription(project.description || '');
        }
    }, [project]);

    if (!project)
        return <p className="text-sm text-[#7a8077]">{t('Loading...')}</p>;
    if (!projectId)
        return (
            <p className="text-sm text-red-600">{t('Project ID is missing')}</p>
        );

    // Edited in place: Save only lights up once something actually changed.
    const changed =
        name.trim() !== project.name ||
        description.trim() !== (project.description || '');

    const handleSave = () => {
        updateProject({
            projectId,
            data: { name: name.trim(), description: description.trim() },
        });
    };

    return (
        <div className="space-y-6">
            <Section title={t('Project information')}>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="edit-name">{t('Project name')}</Label>
                        <Input
                            id="edit-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label htmlFor="edit-desc">{t('Description')}</Label>
                        <Textarea
                            id="edit-desc"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="mt-1.5"
                            rows={3}
                        />
                    </div>
                    {changed && (
                        <div className="flex gap-2 pt-1">
                            <Button
                                size="sm"
                                onClick={handleSave}
                                disabled={isPending || !name.trim()}
                                className="bg-[#2d6a4f] hover:bg-[#245741] text-white"
                            >
                                {isPending && (
                                    <LoaderIcon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                )}
                                {t('Save changes')}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    setName(project.name);
                                    setDescription(project.description || '');
                                }}
                            >
                                {t('Cancel')}
                            </Button>
                        </div>
                    )}
                </div>
            </Section>

            <RoleList />

            <Section title={t('Danger zone')}>
                <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[#1c2620]">
                        {project.softDelete
                            ? t('Restore this project')
                            : t('Delete this project')}
                    </p>
                    {project.softDelete ? (
                        <Button
                            variant="outline"
                            onClick={() => restoreProject(projectId)}
                            disabled={isRestoring}
                        >
                            <RotateCcwIcon className="h-4 w-4 mr-1.5" />
                            {t('Restore project')}
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteConfirm(true)}
                            className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                        >
                            <Trash2Icon className="h-4 w-4 mr-1.5" />
                            {t('Delete project')}
                        </Button>
                    )}
                </div>
            </Section>

            <ConfirmDialog
                open={showDeleteConfirm}
                title={t('Delete project')}
                description={t(
                    'The project will disappear for its members. Super admins still see it and can restore it.',
                )}
                confirmLabel={t('Delete project')}
                onConfirm={() => {
                    deleteProject(projectId);
                    setShowDeleteConfirm(false);
                    navigate(`${BASE}/project`);
                }}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </div>
    );
}
